import _classCallCheck from "@babel/runtime/helpers/classCallCheck";
import _createClass from "@babel/runtime/helpers/createClass";
import _defineProperty from "@babel/runtime/helpers/defineProperty";
import SafeSessionStorage from './storage/SafeSessionStorage';

// WARNING: The value of INTERACTION_SESSION_ID_STORAGE_KEY will be used by Analytics Web Client to attach the session ID to events.
// If you change this, you will need to update the Analytics Web Client code as well.
var INTERACTION_SESSION_ID_STORAGE_KEY = 'interactionSession.id';
var INTERACTION_SESSION_ID_PREV_STORAGE_KEY = 'interactionSession.id.prev';
var INTERACTION_SESSION_EXPIRY_STORAGE_KEY = 'interactionSession.expiry';
export var DEFAULT_SESSION_EXPIRY_TIME_MS = 30 * 60 * 1000; // 30 minutes
export var DEFAULT_EXPIRY_DEBOUNCE = 5 * 1000; // 5 seconds
export var DEFAULT_DEBOUNCE_THRESHOLD_PERCENTAGE = 0.9; // 90% of expiry
export var NOOP = function NOOP() {};
var INTEGER_REGEX = /^\d+$/;
export var INTERACTION_SESSION_ID_UPDATED_EVENT = 'interactionSessionIdUpdated';

/**
 * This class is essentially a wrapper around SessionStorage that introduces some failsafes (such as switching to InMemoryStorage if SessionStorage is not available)
 * and providing resilient methods for accessing and updating the interaction session ID.
 * This class can be initialised anywhere within a document, and `getCurrentInteractionSessionId` will always return the latest interaction session ID.
 *
 * The `updateSettings` method will overwrite the SessionStorage with the persisted session ID, and is only intended to be used by the Analytics Web Client
 */
var InteractionSessionTracking = /*#__PURE__*/function () {
  // This callback function will be called whenever a new session ID is generated
  // It expects a function that sends an operational analytics event, with
  // the first parameter being set to the action ('started' or 'renewed'), and the second parameter being
  // an InteractionSession object containing the new session ID and previous session ID.

  function InteractionSessionTracking() {
    var _this = this;
    _classCallCheck(this, InteractionSessionTracking);
    _defineProperty(this, "analyticsReady", false);
    _defineProperty(this, "generateNewSessionId", function (persistedSessionId) {
      var currentSessionId = _this.safeSessionStorage.getItem(INTERACTION_SESSION_ID_STORAGE_KEY);
      var newSessionId = persistedSessionId ? persistedSessionId : Date.now().toString();
      if (currentSessionId === newSessionId) {
        // If we are already on the required session ID, just return it and make no changes
        // This can happen if the persisted session ID in options is the same as the current session ID
        return currentSessionId;
      }
      _this.safeSessionStorage.setItem(INTERACTION_SESSION_ID_STORAGE_KEY, newSessionId);

      // For generated IDs, send interactionSession event and store previous ID
      if (!persistedSessionId) {
        if (currentSessionId) {
          // Store a copy of the current session ID so we can link sessions if needed
          _this.safeSessionStorage.setItem(INTERACTION_SESSION_ID_PREV_STORAGE_KEY, currentSessionId);
          _this.sendInteractionSessionEvent('renewed');
        } else {
          _this.sendInteractionSessionEvent('started');
        }
      } else {
        // For persisted IDs, remove any existing previous ID link
        _this.safeSessionStorage.removeItem(INTERACTION_SESSION_ID_PREV_STORAGE_KEY);
      }

      // Event listener is in useCrossProductUrl.tsx to update URL query params
      document.dispatchEvent(new Event(INTERACTION_SESSION_ID_UPDATED_EVENT));
      return newSessionId;
    });
    _defineProperty(this, "updateSessionExpiry", function () {
      var expiry = Date.now() + _this.sessionExpiryTime;
      _this.safeSessionStorage.setItem(INTERACTION_SESSION_EXPIRY_STORAGE_KEY, expiry.toString());
      return expiry;
    });
    _defineProperty(this, "debouncedUpdateSessionExpiry", function (currentExpiry) {
      var updateSessionExpiry = _this.updateSessionExpiry;
      // If the current expiry is out of date by more than 10% of the expiry, update immediately
      if (currentExpiry < Date.now() + _this.sessionExpiryDebounceThresholdPercentage * _this.sessionExpiryTime) {
        updateSessionExpiry();
      } else {
        if (_this.sessionExpiryDebounceTimer) {
          clearTimeout(_this.sessionExpiryDebounceTimer);
        }
        // @ts-ignore Something wrong with our types... this is legit
        _this.sessionExpiryDebounceTimer = setTimeout(function () {
          updateSessionExpiry();
        }, _this.sessionExpiryDebounce);
      }
    });
    this.sessionExpiryTime = DEFAULT_SESSION_EXPIRY_TIME_MS;
    this.sessionExpiryDebounce = DEFAULT_EXPIRY_DEBOUNCE;
    this.sessionExpiryDebounceThresholdPercentage = DEFAULT_DEBOUNCE_THRESHOLD_PERCENTAGE;
    this.safeSessionStorage = new SafeSessionStorage();
  }
  return _createClass(InteractionSessionTracking, [{
    key: "getCurrentInteractionSessionId",
    value: function getCurrentInteractionSessionId() {
      if (!this.analyticsReady) {
        return null;
      }
      var sessionId = this.safeSessionStorage.getItem(INTERACTION_SESSION_ID_STORAGE_KEY);
      var sessionExpiry = parseInt(this.safeSessionStorage.getItem(INTERACTION_SESSION_EXPIRY_STORAGE_KEY) || '0', 10) || 0;
      this.debouncedUpdateSessionExpiry(sessionExpiry);
      if (!sessionId || sessionExpiry <= Date.now() || Number.isNaN(sessionExpiry)) {
        return this.generateNewSessionId();
      }
      return sessionId;
    }
  }, {
    key: "getPreviousInteractionSessionId",
    value: function getPreviousInteractionSessionId() {
      if (!this.analyticsReady) {
        return null;
      }
      return this.safeSessionStorage.getItem(INTERACTION_SESSION_ID_PREV_STORAGE_KEY);
    }
  }, {
    key: "setInteractionSessionId",
    value: function setInteractionSessionId(persistedSessionId) {
      if (this.analyticsReady && this.validateSessionId(persistedSessionId)) {
        this.generateNewSessionId(persistedSessionId);
        this.debouncedUpdateSessionExpiry(0);
        return true;
      }
      return false;
    }

    // Must be called with a valid sendEvent function for other methods to work
  }, {
    key: "setAnalyticsCallback",
    value: function setAnalyticsCallback(sendEventCallback) {
      if (this.analyticsReady) {
        // If we already have a valid callback, don't over-write it
        return;
      }
      if (typeof sendEventCallback === 'function') {
        this.sendEventCallback = sendEventCallback;
        this.analyticsReady = true;
      } else {
        this.analyticsReady = false;
      }
    }
  }, {
    key: "validateSessionId",
    value: function validateSessionId(sessionId) {
      // Check if persisted session ID can pass as valid Unix timestamp that is not in the future
      if (sessionId && INTEGER_REGEX.test(sessionId) && parseInt(sessionId, 10) <= Date.now()) {
        return true;
      } else {
        return false;
      }
    }
  }, {
    key: "sendInteractionSessionEvent",
    value: function sendInteractionSessionEvent(action) {
      this.sendEventCallback && this.sendEventCallback({
        action: action,
        actionSubject: 'interactionSession',
        source: 'interactionComponent',
        tags: ['navigationExperiences'] // InteractionSession events will be registered under this platform product
      });
    }
  }]);
}();
export { InteractionSessionTracking as default };